import { Mail, ArrowRight, ExternalLink } from "lucide-react";
import Logo from "../assets/quillstudio-logo-cropped.png";

const Footer = () => {
  return (
    <footer className="relative bg-slate-950 text-slate-400">
      {/* Gradient top border */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">

        {/* Mini CTA banner */}
        <div className="mb-14 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 px-6 py-5">
          <div>
            <p className="text-white font-semibold text-sm">Ready to automate your document workflow?</p>
            <p className="text-slate-400 text-xs mt-0.5">Install QuillStudio free on Salesforce AppExchange.</p>
          </div>
          <a
            href="https://appexchange.salesforce.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all hover:scale-[1.02]"
          >
            Install Free <ArrowRight size={15} />
          </a>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-2 gap-10 md:grid-cols-3">

          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="inline-block bg-white rounded-xl px-3 py-2">
              <img src={Logo} alt="QuillStudio Logo" className="h-8 w-auto object-contain" />
            </div>
            <p className="mt-4 text-sm leading-relaxed">
              The most powerful document automation tool for Salesforce. Transform Word templates into professional PDFs with real customer data.
            </p>
            {/* Social links */}
            <div className="mt-5 flex items-center gap-3">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-slate-400 hover:border-orange-500/50 hover:text-orange-400 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
                </svg>
              </a>
              <a
                href="mailto:sales@exceller.tech"
                aria-label="Email us"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-slate-400 hover:border-orange-500/50 hover:text-orange-400 transition-colors"
              >
                <Mail size={16} />
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-orange-400 mb-4">Product</h4>
            <ul className="space-y-3 text-sm">
              {[
                { label: "Features",       href: "#features" },
                { label: "Pricing",        href: "#pricing" },
                { label: "How It Works",   href: "#how-it-works" },
                { label: "Documentation",  href: "#docs" },
              ].map(({ label, href }) => (
                <li key={label}>
                  <a href={href} className="hover:text-orange-400 transition-colors">{label}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-pink-400 mb-4">Support</h4>
            <ul className="space-y-3 text-sm">
              {[
                { label: "Help Center",   href: "#help" },
                { label: "Contact Us",    href: "mailto:sales@exceller.tech" },
                { label: "AppExchange",   href: "https://appexchange.salesforce.com", external: true },
                { label: "Exceller Tech", href: "https://exceller.tech", external: true },
              ].map(({ label, href, external }) => (
                <li key={label}>
                  <a
                    href={href}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noopener noreferrer" : undefined}
                    className="inline-flex items-center gap-1 hover:text-orange-400 transition-colors"
                  >
                    {label}
                    {external && <ExternalLink size={11} className="opacity-60" />}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 border-t border-white/10 pt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xs text-slate-500">
          <p>
            © {new Date().getFullYear()} QuillStudio. All rights reserved. ·{" "}
            Managed & Developed by{" "}
            <a href="https://exceller.tech" target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors">
              Exceller Management Consultancy LLP
            </a>
          </p>
          <div className="flex items-center gap-5">
            {["Privacy Policy", "Terms of Service", "Cookie Policy"].map((label) => (
              <a key={label} href="#" className="hover:text-orange-400 transition-colors">{label}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
